import { Link, NavLink, useNavigate } from "react-router-dom";
import { Compass, Trophy, Ticket, User, Plus, Bell, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/api";
import { Logo, UserAvatar } from "@/components/common";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { to: "/events", label: "Discover", id: "discover" },
  { to: "/creators", label: "Creators", id: "creators" },
  { to: "/leaderboard", label: "Leaderboard", id: "leaderboard" },
  { to: "/tickets", label: "My Tickets", id: "my-tickets" },
];

const linkCls = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900 hover:bg-stone-200/60"}`;

const UserMenu = ({ user, logout }) => {
  const nav = useNavigate();
  const go = (p) => () => nav(p);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-user-menu" className="rounded-full ring-2 ring-transparent hover:ring-[#FF5C00]/40 transition-shadow">
        <UserAvatar user={user} className="h-9 w-9" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.name}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-profile" onClick={go("/profile")}>Edit profile</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-my-public" onClick={go(`/creators/${user.id}`)}>My public page</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-my-events" onClick={go("/manage")}>Manage my events</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-tickets" onClick={go("/tickets")}>My tickets</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-privacy" onClick={go("/privacy")}>Privacy policy</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-terms" onClick={go("/terms")}>Terms of service</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-delete-account" onClick={go("/account/delete")} className="text-red-600 focus:text-red-700">Delete account</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const BellLink = () => {
  const { data } = useApi("/notifications", { refetchInterval: 60000 });
  return (
    <Link to="/notifications" data-testid="nav-notifications" className="relative p-2 rounded-full hover:bg-stone-200/60 transition-colors">
      <Bell className="h-5 w-5" />
      {data?.unread > 0 && (
        <span data-testid="nav-notifications-count" className="absolute -top-0.5 -right-0.5 bg-[#FF5C00] text-white text-[10px] font-mono rounded-full h-4 min-w-4 px-1 grid place-items-center">{data.unread}</span>
      )}
    </Link>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-stone-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
          <Logo className="h-8 w-8" />
          <span className="font-display font-extrabold text-lg tracking-tight">Creator Series</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end className={linkCls} data-testid={`nav-link-${l.id}`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <>
              <Link to="/events/new" data-testid="nav-btn-create-event" className="hidden sm:inline-flex items-center gap-1.5 bg-[#FF5C00] hover:bg-[#E04E00] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                <Plus className="h-4 w-4" /> Host event
              </Link>
              <BellLink />
              <UserMenu user={user} logout={logout} />
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-btn-login" className="text-sm font-semibold px-4 py-2 rounded-full hover:bg-stone-200/60 transition-colors">Log in</Link>
              <Link to="/register" data-testid="nav-btn-auth" className="text-sm font-semibold px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-[#FF5C00] transition-colors">Join</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const MOBILE = [
  { to: "/events", icon: Compass, label: "Discover", id: "discover" },
  { to: "/creators", icon: Users, label: "Creators", id: "creators" },
  { to: "/events/new", icon: Plus, label: "Host", id: "host" },
  { to: "/leaderboard", icon: Trophy, label: "Ranks", id: "leaderboard" },
  { to: "/tickets", icon: Ticket, label: "Tickets", id: "tickets" },
  { to: "/profile", icon: User, label: "Me", id: "profile" },
];

export const MobileNav = () => (
  <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur-xl border-t border-stone-200 grid grid-cols-6 pb-[env(safe-area-inset-bottom)]">
    {MOBILE.map(({ to, icon: Icon, label, id }) => (
      <NavLink key={to} to={to} end data-testid={`mobile-nav-${id}`}
        className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${isActive ? "text-[#FF5C00]" : "text-stone-500"}`}>
        <Icon className="h-5 w-5" /> {label}
      </NavLink>
    ))}
  </nav>
);

export const Footer = () => (
  <footer className="relative overflow-hidden bg-[#1C1917] text-stone-300 mt-10 pb-20 md:pb-0">
    <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-[#FF5C00]/25 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr]">
      <div className="col-span-2 md:col-span-1">
        <div className="flex items-center gap-3"><Logo className="h-10 w-10" /><span className="font-display font-black text-3xl text-white tracking-tight">Creator Series</span></div>
        <p className="mt-4 max-w-sm text-sm text-stone-400">Small IRL events hosted by local creators. The same account works here and in the mobile app.</p>
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-mono text-xs uppercase tracking-widest text-stone-500">Explore</p>
        <Link to="/events" className="block hover:text-white">Discover events</Link>
        <Link to="/creators" className="block hover:text-white">Creators</Link>
        <Link to="/leaderboard" className="block hover:text-white">Leaderboard</Link>
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-mono text-xs uppercase tracking-widest text-stone-500">Members</p>
        <Link to="/tickets" className="block hover:text-white">My tickets</Link>
        <Link to="/events/new" className="block hover:text-white">Host an event</Link>
        <Link to="/profile" className="block hover:text-white">Profile</Link>
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-mono text-xs uppercase tracking-widest text-stone-500">Legal</p>
        <Link to="/terms" data-testid="footer-terms" className="block hover:text-white">Terms of service</Link>
        <Link to="/privacy" data-testid="footer-privacy" className="block hover:text-white">Privacy policy</Link>
        <Link to="/sms" data-testid="footer-sms" className="block hover:text-white">Text messages</Link>
        <Link to="/account/delete" data-testid="footer-delete-account" className="block hover:text-white">Delete account</Link>
      </div>
    </div>
    <div className="relative border-t border-stone-800 py-5 text-center font-mono text-[11px] text-stone-500">© {new Date().getFullYear()} Yappr LLC · Creator Series · <a href="mailto:support@yapprapp.com" data-testid="footer-support-email" className="hover:text-white">support@yapprapp.com</a></div>
  </footer>
);
